import { useState, useEffect } from 'react'
import { Eye, X } from 'lucide-react'
import {
  Dialog,
  DialogContent,
} from '@/components/ui/dialog'
import { IntuiButton } from '@/components/intueri/IntuiButton'
import { IntuiInput } from '@/components/intueri/IntuiInput'
import { useTranslation } from '@/i18n'
import DocumentPreviewOverlay from './DocumentPreviewOverlay'
import type { DocumentRecord } from '@/types/catalog'

interface MergeAsSetModalProps {
  isOpen: boolean
  onClose: () => void
  documents: DocumentRecord[]
  onMerge: (setName: string, docIds: string[]) => void
}

export default function MergeAsSetModal({
  isOpen,
  onClose,
  documents,
  onMerge,
}: MergeAsSetModalProps) {
  const { t } = useTranslation()
  const [setName, setSetName] = useState('')
  const [selectedDocs, setSelectedDocs] = useState<DocumentRecord[]>([])
  const [previewDoc, setPreviewDoc] = useState<DocumentRecord | null>(null)

  useEffect(() => {
    if (isOpen) {
      setSetName('')
      setSelectedDocs([...documents])
      setPreviewDoc(null)
    }
  }, [isOpen, documents])

  const removeDoc = (docId: string) => {
    setSelectedDocs((prev) => prev.filter((d) => d.id !== docId))
  }

  const handleMerge = () => {
    const trimmed = setName.trim()
    if (!trimmed || selectedDocs.length < 2) return
    onMerge(trimmed, selectedDocs.map((d) => d.id))
  }

  return (
    <>
      <Dialog open={isOpen && !previewDoc} onOpenChange={(open) => { if (!open) onClose() }}>
        <DialogContent
          className="max-w-[560px] p-6 gap-4 flex flex-col"
          hideDefaultClose
        >
          {/* Header */}
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-foreground">
              {t('data_catalog:merge_set.title')}
            </h2>
            <button
              type="button"
              onClick={onClose}
              className="p-0.5 rounded hover:bg-muted transition-colors"
            >
              <X className="size-5 text-muted-foreground" />
            </button>
          </div>

          {/* Set Name Field */}
          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium text-foreground">
              {t('data_catalog:merge_set.set_name_label')}<span className="text-foreground">*</span>
            </label>
            <IntuiInput
              value={setName}
              onChange={(e) => setSetName(e.target.value)}
              placeholder={t('data_catalog:merge_set.set_name_placeholder')}
              className="h-9"
              onKeyDown={(e) => { if (e.key === 'Enter') handleMerge() }}
              autoFocus
            />
          </div>

          {/* Selected Files */}
          <div className="flex flex-col">
            <div className="pb-2">
              <span className="text-sm font-medium text-foreground">
                {t('data_catalog:merge_set.selected_files_label')}
              </span>
            </div>
            <div className="max-h-[416px] overflow-y-auto">
              {selectedDocs.map((doc) => (
                <div
                  key={doc.id}
                  className="flex items-center gap-2 px-2 py-4 border-b border-[#e5e5e5] dark:border-dark-border"
                >
                  <span className="flex-1 min-w-0 text-sm font-medium text-foreground truncate">
                    {doc.name}
                  </span>
                  <button
                    type="button"
                    onClick={() => setPreviewDoc(doc)}
                    className="shrink-0 p-0.5 rounded hover:bg-muted transition-colors"
                  >
                    <Eye className="size-5 text-muted-foreground" />
                  </button>
                  <button
                    type="button"
                    onClick={() => removeDoc(doc.id)}
                    className="shrink-0 p-0.5 rounded hover:bg-muted transition-colors"
                  >
                    <X className="size-5 text-muted-foreground" />
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-2 pt-2">
            <IntuiButton variant="outline" className="h-9" onClick={onClose}>
              {t('common:buttons.cancel')}
            </IntuiButton>
            <IntuiButton
              className="h-9"
              onClick={handleMerge}
              disabled={!setName.trim() || selectedDocs.length < 2}
            >
              {t('data_catalog:merge_set.merge_button')}
            </IntuiButton>
          </div>
        </DialogContent>
      </Dialog>

      {previewDoc && (
        <DocumentPreviewOverlay
          docId={previewDoc.id}
          docName={previewDoc.name}
          onClose={() => setPreviewDoc(null)}
        />
      )}
    </>
  )
}
